import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Zap } from 'lucide-react';
import { adminApi } from '../../lib/api';
import { toast } from 'sonner';

const GRADES = [5, 6, 7, 8, 9, 10, 11, 12];

export const FeatureFlags = () => {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);

  useEffect(() => { fetchFlags(); }, []);

  const fetchFlags = async () => {
    setLoading(true);
    try {
      const data = await adminApi.getFeatureFlags();
      setFlags(data.flags || []);
    } catch { toast.error('Failed to load feature flags'); }
    finally { setLoading(false); }
  };

  const handleToggle = async (flagId, enabled) => {
    setToggling(flagId);
    try {
      await adminApi.toggleFeatureFlag(flagId, enabled);
      setFlags(prev => prev.map(f => f.flag_id === flagId ? { ...f, enabled } : f));
      toast.success(`Feature ${enabled ? 'enabled' : 'disabled'}`);
    } catch { toast.error('Failed to toggle feature'); }
    finally { setToggling(null); }
  };

  if (loading) return <div className="skeleton h-64 rounded-lg" />;

  const moduleFlags = flags.filter(f => f.category === 'module');
  const featureFlags = flags.filter(f => f.category !== 'module');

  const renderFlagGroup = (title, description, items) => (
    <div className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold">{title}</h2>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      {items.map(flag => (
        <div key={flag.flag_id} className="p-4 rounded-xl border bg-card flex items-center justify-between" data-testid={`flag-${flag.flag_id}`}>
          <div className="flex-1 mr-4">
            <div className="flex items-center gap-2 mb-0.5">
              <h3 className="font-semibold text-sm">{flag.name}</h3>
              <Badge variant={flag.enabled ? 'default' : 'secondary'} className={`text-[10px] ${flag.enabled ? 'bg-emerald-600' : ''}`}>{flag.enabled ? 'ON' : 'OFF'}</Badge>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">{flag.description}</p>
            {flag.updated_at && <p className="text-[10px] text-muted-foreground/60 mt-1">Last updated: {new Date(flag.updated_at).toLocaleString()}</p>}
          </div>
          <Switch
            data-testid={`toggle-${flag.flag_id}`}
            checked={flag.enabled}
            disabled={toggling === flag.flag_id}
            onCheckedChange={(checked) => handleToggle(flag.flag_id, checked)}
          />
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-8" data-testid="feature-flags-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Zap className="w-6 h-6" /> Feature Flags</h1>
        <p className="text-muted-foreground text-sm mt-1">Enable or disable platform features and modules. Changes take effect immediately for all users.</p>
      </div>
      {moduleFlags.length > 0 && renderFlagGroup(
        'Platform Modules',
        'Toggle entire sections of the platform on or off. Disabled modules are hidden from navigation and inaccessible to students.',
        moduleFlags
      )}
      {featureFlags.length > 0 && renderFlagGroup(
        'Feature Flags',
        'Fine-grained control over specific behaviors within modules.',
        featureFlags
      )}
    </div>
  );
};


// ==================== QUESTION BANK ====================
